import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Button, ButtonModel, Spinner } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'

// components
import Step0ServiceSelection from './Step0ServiceSelection/Step0ServiceSelection'
import Step1ServiceParameterSelection from './Step1ServiceParameterSelection/Step1ServiceParameterSelection'
import Step2EmployeeAndDateSelection from './Step2EmployeeAndDateSelection/Step2EmployeeAndDateSelection'
import Step3BookingConfirmation from './Step3BookingConfirmation/Step3BookingConfirmation'
import Step3ReservationsSummary from './Step3ReservationsSummary/Step3ReservationsSummary'
import Step4Summary from './Step4Summary/Step4Summary'
import ReservationBookingModalSkeleton from './ReservationBookingModalSkeleton/ReservationBookingModalSkeleton'
import LoginScreen from '../../atoms/LoginScreen/LoginScreen'
import Modal, { ModalModel } from '../../atoms/Modal/Modal'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'
import { RESERVATION_BOOKING_MODAL_TITLE_ID } from './ReservationBookingModalLayout/ReservationBookingModalLayout'

// types
import {
	CmsReservationBookingModalData,
	ConfigResponse,
	DocumentsResponse,
	GetCartResponse,
	GetPaginatedCalendarEventsGroupReservationAvailableParams,
	GetPaginatedCalendarEventsGroupReservationAvailableResponse,
	PostCartSubmitSuccessResponse,
	PostCartGroupReservationBody,
	PostCartReservationBody,
	ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm,
	ReservationBookingEmployeeAndDateSelectionSubmittedForm,
	ReservationBookingModalCloseType,
	ReservationBookingModalInitData,
	ReservationBookingReservationsSummaryForm,
	ReservationBookingSelectedFormValues,
	ReservationBookingServiceParameterSelectionForm,
	SalonResponse,
	SalonServiceResponse,
	SalonServicesResponse,
	ServiceCardCallbackData
} from '../../types/types'

// utils
import {
	DEFAULT_DATE_FORMAT,
	DOCUMENTS,
	LOCALE_STORAGE_KEYS,
	MSG_TYPE,
	CART_RESERVATION_SUBMIT_ERROR_CODE,
	RESERVATION_BOOKING_MODAL_STEP,
	RESERVATIONS_TIME_SLOTS,
	SCREEN_STATE_KEY,
	SERVICE_TYPE
} from '../../utils/enums'
import {
	getCartGroupReservationRequestBody,
	getCartReservationRequestBody,
	getCartReservationSubmitErrorCode,
	getPutCartGroupReservationRequestBody,
	getPutCartReservationRequestBody,
	SelectedIndustryData,
	submitCartReservationRequest,
	submitPutCartReservationRequest
} from './reservationBookingModalHelpers'
import { AppContext } from '../../utils/appProvider'
import {
	ERROR_MESSAGES,
	findServiceCategoryById,
	getPhoneNumber,
	hasMultipleIndustriesSameService,
	showErrorNotifications,
	showNotifications
} from '../../utils/helper'
import { getAccountEvent, getModalBackButtonEvent, getModalClosedEvent, getReservationSelectServiceEvent } from '../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../utils/dataLayer'
import { LOGIN_AND_REGISTRATION_BANNER_FALLBACKS, PARAMETER_SELECTION_BANNER_FALLBACKS, THANK_YOU_PAGE_BANNER_FALLBACKS } from '../../utils/cmsFallbacks'
import type { ReservationDeviceStorage } from '../../utils/reservationDeviceId'

// hooks
import useAuthRedirect from '../../hooks/useAuthRedirect'
import useLocalStorage from '../../hooks/useLocalStorage'

// styles
import { RESERVATION_BOOKING_MODAL_CLASS_NAME } from '../../styles/constants'
import * as SC from './ReservationBookingModalStyles'
import Step2EmployeeAndDateSelectionGroup from './Step2EmployeeAndDateSelectionGroup/Step2EmployeeAndDateSelectionGroup'
import ReservationBookingDialog from './ReservationBookingDialog/ReservationBookingDialog'
import * as DialogSC from './ReservationBookingDialog/ReservationBookingDialogStyles'
import useReservationBookingModalFocus from './useReservationBookingModalFocus'
import { hasValidStep4SummaryData } from './Step4Summary/step4SummaryHelpers'

type Props = {
	initData: ReservationBookingModalInitData
	configData: ConfigResponse
	salonData: {
		id: string
		availableReservationSystem: boolean
		images: NonNullable<SalonResponse['salon']>['images']
		phones: NonNullable<SalonResponse['salon']>['phones']
	}
	salonServicesData: SalonServicesResponse
	isOpen: boolean
	onClose: () => void
	usePersistedValue?: boolean
}

const DEFAULT_GROUP_SLOTS_LIMIT = 10

const hasCompletedClassicTimeSelection = (employeeAndDateSelectionData: ReservationBookingModalInitData['employeeAndDateSelectionData']) =>
	!!(employeeAndDateSelectionData?.date && employeeAndDateSelectionData?.timeSlot?.timeFrom && employeeAndDateSelectionData?.timeSlot?.timeTo)

const hasCompletedGroupTimeSelection = (employeeAndDateGroupSelectionData: ReservationBookingModalInitData['employeeAndDateGroupSelectionData']) =>
	!!(employeeAndDateGroupSelectionData?.date && employeeAndDateGroupSelectionData?.eventID)

const LOGIN_RESTORE_STEPS: RESERVATION_BOOKING_MODAL_STEP[] = [
	RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION,
	RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION,
	RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION
]

const getCartSessionInitialStep = (initCurrentStep: ReservationBookingModalInitData['initCurrentStep']) => {
	if (initCurrentStep && LOGIN_RESTORE_STEPS.includes(initCurrentStep)) {
		return initCurrentStep
	}

	return null
}

const getInitialStep = (data: ReservationBookingModalInitData, isGroupReservation: boolean, hasActiveCartSession: boolean) => {
	const { employeeAndDateSelectionData, initServiceCategoryParameterID, initServiceID, employeeAndDateGroupSelectionData } = data

	if (hasActiveCartSession) {
		const cartSessionStep = getCartSessionInitialStep(data.initCurrentStep)

		if (cartSessionStep) {
			return cartSessionStep
		}
	}

	if (isGroupReservation) {
		if (hasCompletedGroupTimeSelection(employeeAndDateGroupSelectionData)) {
			return RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION
		}
	} else if (hasCompletedClassicTimeSelection(employeeAndDateSelectionData)) {
		return RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION
	}

	if (initServiceCategoryParameterID) {
		return RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION
	}

	if (initServiceID) {
		if (isGroupReservation) {
			return RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION
		}
		return RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION
	}

	return RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION
}

type InitSelectedFormValuesArgs = {
	defaultPhonePrefixCountryCode: string
	serviceEmployees: SalonServiceResponse['service']['employees']
	initData: Pick<
		ReservationBookingModalInitData,
		'parameterSelectionData' | 'bookingConfirmationData' | 'reservationsSummaryData' | 'employeeAndDateSelectionData' | 'employeeAndDateGroupSelectionData'
	> | null
}

const DEFAULT_SELECTED_FORM_VALUES = (args: InitSelectedFormValuesArgs): ReservationBookingSelectedFormValues => {
	const { defaultPhonePrefixCountryCode, serviceEmployees, initData } = args
	const { parameterSelectionData, employeeAndDateSelectionData, bookingConfirmationData, employeeAndDateGroupSelectionData, reservationsSummaryData } =
		initData || {}
	const { date, employeeID, timeSlot } = employeeAndDateSelectionData || {}
	const validInitDate = date && dayjs(date).isValid() ? dayjs(date) : dayjs()
	let validInitEmployeeID = null

	if (serviceEmployees.length === 1) {
		// if only one employee is assigned to the service, we will preselect him
		validInitEmployeeID = serviceEmployees[0].id
	}

	if (employeeID) {
		// check if employee still exists in the service and if not use currently assigned value
		validInitEmployeeID = serviceEmployees.find((employee) => employee.id === employeeID)?.id || validInitEmployeeID
	}

	let validTimeSlot: ReservationBookingEmployeeAndDateSelectionSubmittedForm['timeSlot'] = {
		timeFrom: '',
		timeTo: '',
		employeeID: '',
		dayPeriod: RESERVATIONS_TIME_SLOTS.MORNING
	}

	if (validInitDate && timeSlot) {
		// time slots data could be initialized only when there is a valid init date
		let validTimeSlotEmployee = ''

		if (serviceEmployees.length === 1) {
			// if only one employee is assigned to the service, we will preselect him
			validTimeSlotEmployee = serviceEmployees[0].id
		}

		// check if employee still exists in the service and if not use currently assigned value
		validTimeSlotEmployee = serviceEmployees.find((employee) => employee.id === timeSlot.employeeID)?.id || validTimeSlotEmployee

		validTimeSlot = {
			...validTimeSlot,
			timeFrom: timeSlot.timeFrom,
			timeTo: timeSlot.timeTo,
			employeeID: validTimeSlotEmployee,
			dayPeriod: timeSlot.dayPeriod || validTimeSlot.dayPeriod
		}
	}

	// Process group reservation data
	const { date: groupDate, employeeID: groupEmployeeID, timeSlot: groupTimeSlot, eventID } = employeeAndDateGroupSelectionData || {}
	const validGroupInitDate = groupDate && dayjs(groupDate).isValid() ? dayjs(groupDate) : dayjs()
	let validGroupInitEmployeeID = null

	if (serviceEmployees.length === 1) {
		// if only one employee is assigned to the service, we will preselect him
		validGroupInitEmployeeID = serviceEmployees[0].id
	}

	if (groupEmployeeID) {
		// check if employee still exists in the service and if not use currently assigned value
		validGroupInitEmployeeID = serviceEmployees.find((employee) => employee.id === groupEmployeeID)?.id || validGroupInitEmployeeID
	}

	let validGroupTimeSlot: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm['timeSlot'] = {
		timeFrom: '',
		timeTo: '',
		employeeID: ''
	}

	if (validGroupInitDate && groupTimeSlot) {
		// time slots data could be initialized only when there is a valid init date
		let validGroupTimeSlotEmployee = ''

		if (serviceEmployees.length === 1) {
			// if only one employee is assigned to the service, we will preselect him
			validGroupTimeSlotEmployee = serviceEmployees[0].id
		}

		// check if employee still exists in the service and if not use currently assigned value
		validGroupTimeSlotEmployee = serviceEmployees.find((employee) => employee.id === groupTimeSlot.employeeID)?.id || validGroupTimeSlotEmployee

		validGroupTimeSlot = {
			timeFrom: groupTimeSlot.timeFrom,
			timeTo: groupTimeSlot.timeTo,
			employeeID: validGroupTimeSlotEmployee
		}
	}

	return {
		[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION]: {
			serviceCategoryParameterValueID: parameterSelectionData?.serviceCategoryParameterValueID ?? ''
		},
		[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]: {
			employeeID: validGroupInitEmployeeID,
			eventID: eventID ?? null,
			date: validGroupInitDate.format(DEFAULT_DATE_FORMAT),
			timeSlot: validGroupTimeSlot
		},
		[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]: {
			employeeID: validInitEmployeeID,
			date: validInitDate.format(DEFAULT_DATE_FORMAT),
			timeSlot: validTimeSlot
		},
		[RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY]: {
			note: reservationsSummaryData?.note ?? ''
		},
		[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION]: {
			firstName: bookingConfirmationData?.firstName ?? '',
			lastName: bookingConfirmationData?.lastName ?? '',
			email: bookingConfirmationData?.email ?? '',
			notAllowMarketing: bookingConfirmationData?.notAllowMarketing ?? false,
			phone: {
				phoneNumber: bookingConfirmationData?.phone?.phoneNumber ?? '',
				phonePrefixCountryCode: bookingConfirmationData?.phone?.phonePrefixCountryCode ?? defaultPhonePrefixCountryCode
			},
			agreement: bookingConfirmationData?.agreement ?? false,
			isVisitor: bookingConfirmationData?.isVisitor ?? false,
			note: bookingConfirmationData?.note ?? reservationsSummaryData?.note ?? '',
			visitor: {
				firstName: bookingConfirmationData?.visitor?.firstName ?? '',
				lastName: bookingConfirmationData?.visitor?.lastName ?? ''
			}
		}
	}
}

const ReservationBookingModal = (props: Props) => {
	const { initData, isOpen, onClose, salonData, salonServicesData, configData } = props
	const { initIndustryID, initServiceID, initAvResTimeSlotDayPart } = initData

	const { locale, formatMessage } = useIntl()
	const { apiBrowser, isAuthorized } = useContext(AppContext)

	const defaultPhonePrefixCountryCode =
		configData.rolloutCountries.find((country) => country.languageCode === locale)?.code ?? configData.allCountries[0].code

	const [initialLoading, setIsInitialLoadingState] = useState(true)
	const [isLoadingError, setIsLoadingError] = useState(false)
	const [currentStep, setCurrentStepState] = useState<RESERVATION_BOOKING_MODAL_STEP | null>(null)
	const [cmsData, setCmsData] = useState<CmsReservationBookingModalData>({
		loginAndRegistrationBannerData: LOGIN_AND_REGISTRATION_BANNER_FALLBACKS(),
		parameterSelectionBannerData: PARAMETER_SELECTION_BANNER_FALLBACKS(),
		thankYouPageBannerData: THANK_YOU_PAGE_BANNER_FALLBACKS()
	})
	const [availableGroupSlots, setAvailableGroupSlots] = useState<GetPaginatedCalendarEventsGroupReservationAvailableResponse | null>(null)
	const [isFetchingGroupSlots, setIsFetchingGroupSlots] = useState(false)
	const [groupSlotsError, setGroupSlotsError] = useState(false)

	const [selectedServiceData, setSelectedServiceData] = useState<SalonServiceResponse['service'] | null>(null)
	const [selectedIndustryData, setSelectedIndustryData] = useState<SelectedIndustryData | null>(null)
	const [canContinueWithoutLogin, setCanContinueWithoutLogin] = useState(false)
	const [isContinueAsGuestLoading, setIsContinueAsGuestLoading] = useState(false)
	const [selectedFormValues, setSelectedFormValues] = useState<ReservationBookingSelectedFormValues>(
		DEFAULT_SELECTED_FORM_VALUES({ defaultPhonePrefixCountryCode, serviceEmployees: selectedServiceData?.employees || [], initData })
	)
	const [submittedReservationData, setSubmittedReservationData] = useState<PostCartSubmitSuccessResponse | null>(null)
	const [isAddingAnotherServiceFromSummary, setIsAddingAnotherServiceFromSummary] = useState(false)
	const [isEditingReservationFromSummary, setIsEditingReservationFromSummary] = useState(false)
	const [editingCartReservationItemID, setEditingCartReservationItemID] = useState<string | null>(null)
	const [isLeaveReservationModalOpen, setIsLeaveReservationModalOpen] = useState(false)
	const [isLeavingReservation, setIsLeavingReservation] = useState(false)
	const [isFullCartModalOpen, setIsFullCartModalOpen] = useState(false)
	const [isServiceAlreadyBookedModalOpen, setIsServiceAlreadyBookedModalOpen] = useState(false)
	const [documents, setDocuments] = useState<DocumentsResponse['documents']>()
	const [hasCartReservationItems, setHasCartReservationItems] = useState(false)
	const [hasAcknowledgedUnavailableServices, setHasAcknowledgedUnavailableServices] = useState(false)
	const { containerRef, handoffFocusToDialog } = useReservationBookingModalFocus(isOpen)

	const setCurrentStep = useCallback(
		(step: RESERVATION_BOOKING_MODAL_STEP | null) => {
			// Handoff before remounting steps so the focused control can unmount safely.
			// Skip when clearing step on close so FocusTrap can return focus to the opener.
			if (step !== null && isOpen) {
				handoffFocusToDialog()
			}
			setCurrentStepState(step)
		},
		[handoffFocusToDialog, isOpen]
	)

	const setIsInitialLoading = useCallback(
		(loading: boolean) => {
			if (loading) {
				handoffFocusToDialog()
			}
			setIsInitialLoadingState(loading)
		},
		[handoffFocusToDialog]
	)

	const {
		storedValue: cartReservationDeviceData,
		setValue: setCartReservationDeviceData,
		loadingPersistentData: loadingCartReservationDeviceData
	} = useLocalStorage<ReservationDeviceStorage>(LOCALE_STORAGE_KEYS.DEVICE_ID)
	const isCartActive = Boolean(cartReservationDeviceData?.deviceID) && cartReservationDeviceData?.cartActive !== false
	const phoneNumber = getPhoneNumber(salonData.phones[0], configData.rolloutCountries)
	const selectedServiceCategoryParameterData = selectedServiceData?.serviceCategoryParameter
	const selectedServiceCategoryParameterValueData = selectedServiceCategoryParameterData
		? selectedServiceCategoryParameterData.values.find(
				(value) => selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION].serviceCategoryParameterValueID === value.id
			)
		: undefined
	const isGroupReservation = selectedServiceData?.type === SERVICE_TYPE.CUSTOM_GROUP
	const hasServicesSelectionStep = !initServiceID || isAddingAnotherServiceFromSummary
	const hasParameterSelectionStep = !!selectedServiceData?.serviceCategoryParameter
	const { images } = salonData

	// use salon image as fallback if selected industry is not known
	// this can happen when you open a custom service from the reservation detail page and the service is assigned to more then a one industry
	// we can't determine to which industry service belongs in this case (as opposite to when user goes with standard flow from salon detail page, where it is possible even for the custom service)
	const salonHeaderImage = (images.find((image) => image.isCover) || images[0])?.resizedImages?.medium
	const fallbackHeaderImage = salonHeaderImage
	const headerImage = selectedIndustryData?.image || fallbackHeaderImage

	const isInitRef = useRef(true)

	const getSelectedServiceAndIndustryData = useCallback(
		async (serviceID: string, industryID: string | null, servicesData: SalonServicesResponse['groupedServicesByCategory']) => {
			const serviceData = await apiBrowser.b2c.getSalonServiceData(serviceID)
			const foundIndustryData = servicesData.find((industry) => industry.category?.id === industryID)?.category
			return {
				serviceData: serviceData?.service,
				industryData: foundIndustryData ? { industryID: foundIndustryData.id, image: foundIndustryData.image.resizedImages.medium } : null
			}
		},
		[apiBrowser]
	)

	const fetchGroupSlots = useCallback(
		async (salonID: string, query: GetPaginatedCalendarEventsGroupReservationAvailableParams, append = false) => {
			const { employeeID, serviceID, page, dateFrom } = query

			if (!append) {
				setAvailableGroupSlots(null)
				setGroupSlotsError(false)
			}

			setIsFetchingGroupSlots(true)
			try {
				const result = await apiBrowser.b2c.getPaginatedCalendarEventsGroupReservationAvailable(
					salonID,
					{
						serviceID,
						employeeID,
						dateFrom,
						page,
						limit: DEFAULT_GROUP_SLOTS_LIMIT
					},
					cartReservationDeviceData?.deviceID
				)

				setAvailableGroupSlots((prev) => {
					if (append && prev) {
						return {
							availableReservations: [...prev.availableReservations, ...result.availableReservations],
							pagination: result.pagination
						}
					}

					return result
				})
				return result
			} catch {
				setGroupSlotsError(true)
				return null
			} finally {
				setIsFetchingGroupSlots(false)
			}
		},
		[apiBrowser.b2c, cartReservationDeviceData?.deviceID]
	)

	useEffect(() => {
		;(async () => {
			if (isOpen) {
				if (loadingCartReservationDeviceData) {
					return
				}

				if (isInitRef.current) {
					setHasCartReservationItems(false)

					const applyInitialStep = (isGroupReservationForInit: boolean) => {
						const initialStep = getInitialStep(initData, isGroupReservationForInit, isCartActive)

						setCurrentStep(initialStep)

						if (initialStep === RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION) {
							setCanContinueWithoutLogin(true)
						}

						// Only when resuming an in-progress cart after login redirect — not when landing on Step2
						// because initServiceID skips parameter selection (no cart item exists yet).
						if (isCartActive && getCartSessionInitialStep(initData.initCurrentStep)) {
							setHasCartReservationItems(true)
						}
					}

					if (initServiceID) {
						// if we already know serviceID, the first service selection step is skipped
						// if there is no initIndustryID provided, we'll try to find one from the services list based on the initServiceID
						// industry data could be null in this case
						// this can happen when you open a custom service from the reservation detail page and the service is assigned to more then a one industry

						let industryId = initIndustryID

						if (!initIndustryID && !hasMultipleIndustriesSameService(salonServicesData.groupedServicesByCategory, initServiceID)) {
							const { industryData } = findServiceCategoryById(salonServicesData.groupedServicesByCategory, initServiceID)
							industryId = industryData?.id ?? null
						}

						const { serviceData, industryData } = await getSelectedServiceAndIndustryData(
							initServiceID,
							industryId,
							salonServicesData.groupedServicesByCategory
						)

						if (serviceData) {
							const isGroupReservationForInit = serviceData.type === SERVICE_TYPE.CUSTOM_GROUP

							applyInitialStep(isGroupReservationForInit)

							const defaultValues = DEFAULT_SELECTED_FORM_VALUES({
								defaultPhonePrefixCountryCode,
								serviceEmployees: serviceData.employees,
								initData
							})
							if (isGroupReservationForInit) {
								fetchGroupSlots(salonData.id, {
									serviceID: serviceData.id,
									employeeID: defaultValues.EMPLOYEE_AND_DATE_GROUP_SELECTION.employeeID,
									dateFrom: dayjs().format(DEFAULT_DATE_FORMAT)
								})
							}
							setSelectedServiceData(serviceData)
							setSelectedIndustryData(industryData)
							setSelectedFormValues(defaultValues)
						} else {
							applyInitialStep(false)
							setIsLoadingError(true)
						}
					} else {
						applyInitialStep(false)

						const foundIndustryData =
							salonServicesData.groupedServicesByCategory.find((industry) => industry.category?.id === initIndustryID)?.category ||
							salonServicesData.groupedServicesByCategory[0].category
						const industryData: SelectedIndustryData | null = foundIndustryData
							? { industryID: foundIndustryData.id, image: foundIndustryData.image.resizedImages.medium }
							: null
						// it is necessary that selected industry data exists for the first service selection step
						if (foundIndustryData) {
							setSelectedIndustryData(industryData)
						} else {
							setIsLoadingError(true)
						}
					}
					const [cmsBookingModalData, documentsData] = await Promise.all([
						apiBrowser.cms.getReservationBookingModalData(),
						apiBrowser.b2c.getDocuments({ assetTypes: [DOCUMENTS.B2C_RESERVATIONS_TERMS, DOCUMENTS.B2C_PRIVACY_POLICY] })
					])
					setCmsData(cmsBookingModalData)
					setDocuments(documentsData?.documents)
				}
				isInitRef.current = false
				setIsInitialLoading(false)
			} else {
				isInitRef.current = true
				setIsInitialLoading(true)
			}
		})()
	}, [
		apiBrowser,
		isOpen,
		initServiceID,
		initIndustryID,
		initData,
		salonServicesData.groupedServicesByCategory,
		defaultPhonePrefixCountryCode,
		getSelectedServiceAndIndustryData,
		isAuthorized,
		fetchGroupSlots,
		salonData.id,
		cartReservationDeviceData?.deviceID,
		isCartActive,
		loadingCartReservationDeviceData,
		setCurrentStep,
		setIsInitialLoading
	])

	const onServiceCardButtonClick = async (data: ServiceCardCallbackData) => {
		setIsInitialLoading(true)
		const { serviceData, industryData } = await getSelectedServiceAndIndustryData(
			data.serviceID,
			data.industryID,
			salonServicesData.groupedServicesByCategory
		)

		const event = getReservationSelectServiceEvent({
			event: 'reservation_service',
			category: data.industryID,
			subcategory: data.gaSpecificData.secondLevelCategoryID,
			type: data.gaSpecificData.serviceCategoryID
		})

		pushToDataLayer(event)

		if (!serviceData) {
			setIsInitialLoading(false)
			setIsLoadingError(true)
			return
		}

		const defaultValues = DEFAULT_SELECTED_FORM_VALUES({
			defaultPhonePrefixCountryCode,
			serviceEmployees: serviceData.employees,
			initData: {
				...initData,
				employeeAndDateSelectionData: null,
				parameterSelectionData: null
			}
		})
		if (serviceData.type === SERVICE_TYPE.CUSTOM_GROUP) {
			fetchGroupSlots(salonData.id, {
				serviceID: serviceData.id,
				employeeID: defaultValues.EMPLOYEE_AND_DATE_GROUP_SELECTION.employeeID,
				dateFrom: dayjs().format(DEFAULT_DATE_FORMAT)
			})
		}

		if (isAddingAnotherServiceFromSummary || !selectedServiceData) {
			setSelectedFormValues(defaultValues)
		} else if (selectedServiceData && serviceData.id !== selectedServiceData?.id) {
			setSelectedFormValues(defaultValues)
		}

		setSelectedServiceData(serviceData)
		setSelectedIndustryData(industryData)

		let nextStep: RESERVATION_BOOKING_MODAL_STEP

		if (serviceData.type === SERVICE_TYPE.CUSTOM_GROUP) {
			nextStep = RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION
		} else if (serviceData.serviceCategoryParameter?.id) {
			nextStep = RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION
		} else {
			nextStep = RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION
		}

		setCurrentStep(nextStep)
		setIsInitialLoading(false)
	}

	const handleSubmitParameterSelectionFrom = (values: ReservationBookingServiceParameterSelectionForm) => {
		setSelectedFormValues((prev) => ({ ...prev, [RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION]: { ...values } }))
		if (isGroupReservation) {
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION)
		} else {
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION)
		}
	}

	const clearCartEditState = () => {
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)
	}

	const handleSubmitEmployeeAndDateSelectionForm = (
		values: ReservationBookingEmployeeAndDateSelectionSubmittedForm,
		options?: { keepCartEditState?: boolean }
	) => {
		setSelectedFormValues((prev) => ({ ...prev, [RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]: { ...values } }))
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY)

		if (!options?.keepCartEditState) {
			clearCartEditState()
		}

		if (isAddingAnotherServiceFromSummary) {
			setIsAddingAnotherServiceFromSummary(false)
		}
	}

	const handleSubmitEmployeeAndDateGroupSelectionForm = (
		values: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm,
		options?: { keepCartEditState?: boolean }
	) => {
		setSelectedFormValues((prev) => ({ ...prev, [RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]: { ...values } }))
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY)

		if (!options?.keepCartEditState) {
			clearCartEditState()
		}

		if (isAddingAnotherServiceFromSummary) {
			setIsAddingAnotherServiceFromSummary(false)
		}
	}

	const onCartReservationItemAdded = useCallback(() => {
		setHasCartReservationItems(true)
	}, [])

	const persistEmployeeAndDateCartReservation = async (
		requestBody: PostCartReservationBody,
		submittedData: ReservationBookingEmployeeAndDateSelectionSubmittedForm,
		cartReservationItemID: string | null = editingCartReservationItemID
	) => {
		if (cartReservationItemID) {
			await submitPutCartReservationRequest({
				putCartReservations: apiBrowser.b2c.putCartReservation,
				requestBody: getPutCartReservationRequestBody(cartReservationItemID, {
					date: requestBody.start.date,
					timeFrom: requestBody.start.time,
					timeTo: requestBody.end.time,
					salonID: requestBody.salonID,
					employeeID: requestBody.employeeID,
					serviceID: requestBody.serviceID,
					serviceCategoryParameterValueID: requestBody.serviceCategoryParameterValueID ?? null
				}),
				cartReservationDeviceData,
				setCartReservationDeviceData
			})
		} else {
			await submitCartReservationRequest({
				postCartReservations: apiBrowser.b2c.postCartReservations,
				requestBody,
				cartReservationDeviceData,
				setCartReservationDeviceData
			})
			onCartReservationItemAdded()
		}

		handleSubmitEmployeeAndDateSelectionForm(submittedData)
	}

	const persistEmployeeAndDateGroupCartReservation = async (
		requestBody: PostCartGroupReservationBody,
		submittedData: ReservationBookingEmployeeAndDateGroupSelectionSubmittedForm,
		cartReservationItemID: string | null = editingCartReservationItemID
	) => {
		if (cartReservationItemID) {
			await submitPutCartReservationRequest({
				putCartReservations: apiBrowser.b2c.putCartGroupReservation,
				requestBody: getPutCartGroupReservationRequestBody(cartReservationItemID, requestBody.calendarEventID),
				cartReservationDeviceData,
				setCartReservationDeviceData
			})
		} else {
			await submitCartReservationRequest({
				postCartReservations: apiBrowser.b2c.postCartGroupReservations,
				requestBody,
				cartReservationDeviceData,
				setCartReservationDeviceData
			})
			onCartReservationItemAdded()
		}

		handleSubmitEmployeeAndDateGroupSelectionForm(submittedData)
	}

	const onSubmitBookingConfirmationForm = (submittedData?: PostCartSubmitSuccessResponse) => {
		if (!submittedData) {
			return
		}

		if (!hasValidStep4SummaryData(submittedData)) {
			showNotifications([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
			return
		}

		// Cart is converted to an order — reset cart session; device-id is kept for tracking.
		setCartReservationDeviceData({ cartActive: false })
		setHasCartReservationItems(false)
		setSubmittedReservationData(submittedData)
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SUMMARY)
	}

	const onSubmitReservationsSummaryForm = (values: ReservationBookingReservationsSummaryForm) => {
		const note = values.note?.trim() || ''

		setSelectedFormValues((prev) => ({
			...prev,
			[RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY]: {
				note
			},
			[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION]: {
				...prev[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION],
				note
			}
		}))
		setHasAcknowledgedUnavailableServices(false)
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION)
	}

	const onUnavailableServicesAcknowledged = useCallback(() => {
		setHasAcknowledgedUnavailableServices(true)
	}, [])

	const onInitNoteFromCart = (note: string) => {
		setSelectedFormValues((prev) => ({
			...prev,
			[RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY]: {
				note
			},
			[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION]: {
				...prev[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION],
				note
			}
		}))
	}

	const onAddAnotherService = () => {
		setIsAddingAnotherServiceFromSummary(true)
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)
		// Login/guest step is only required before the first cart item exists.
		if (!hasCartReservationItems) {
			setCanContinueWithoutLogin(false)
		}

		if (!selectedIndustryData) {
			const firstIndustry = salonServicesData.groupedServicesByCategory.find((industry) => industry.category)?.category

			if (firstIndustry) {
				setSelectedIndustryData({ industryID: firstIndustry.id, image: firstIndustry.image.resizedImages.medium })
			}
		}

		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION)
	}

	const onBackFromEditReservation = () => {
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)
		setCanContinueWithoutLogin(true)
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY)
	}

	const getRestoreStepOnLogin = (): RESERVATION_BOOKING_MODAL_STEP | null => {
		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION) {
			return RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION
		}

		// LoginScreen after Step2 confirm — internal step is RESERVATIONS_SUMMARY, restore slot selection.
		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY && !canContinueWithoutLogin) {
			return isGroupReservation
				? RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION
				: RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION
		}

		return null
	}

	const getDataToPersist = (): ReservationBookingModalInitData => {
		return {
			initIndustryID: selectedIndustryData?.industryID ?? null,
			initServiceID: selectedServiceData?.id ?? null,
			initAvResTimeSlotDayPart,
			initServiceCategoryParameterID: selectedServiceCategoryParameterData?.id ?? null,
			parameterSelectionData: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION],
			employeeAndDateSelectionData: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION],
			reservationsSummaryData: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY],
			bookingConfirmationData: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION],
			employeeAndDateGroupSelectionData: selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION],
			initCurrentStep: getRestoreStepOnLogin()
		}
	}

	const { onLogin, onRegister, clearSnapshot } = useAuthRedirect(SCREEN_STATE_KEY.RESERVATION_SUMMARY_MODAL_SALON_CATEGORY_DETAIL, getDataToPersist())

	const onCartReservationItemsChange = useCallback((itemCount: number) => {
		setHasCartReservationItems(itemCount > 0)
	}, [])

	/**
	 * Loads cart from BE; deletes it only when it contains reservation items.
	 * Returns the last cart item for navigation, or undefined when the cart is empty / missing.
	 */
	const clearServerCartAndGetLastItem = async (): Promise<GetCartResponse['cart']['cartReservationItems'][number] | undefined> => {
		const deviceID = cartReservationDeviceData?.deviceID

		if (!deviceID) {
			return undefined
		}

		const deleteServerCartSilently = async () => {
			try {
				await apiBrowser.b2c.deleteCart(deviceID, { displayNotification: false })
			} catch {
				// Cart may already be gone — best-effort release of held reservations.
			}
		}

		try {
			const cartResponse = await apiBrowser.b2c.getCart(deviceID)
			const cartItems = cartResponse.cart.cartReservationItems ?? []

			if (cartItems.length === 0) {
				return undefined
			}

			const lastCartItem = cartItems[cartItems.length - 1]
			await deleteServerCartSilently()

			return lastCartItem
		} catch {
			await deleteServerCartSilently()
			return undefined
		}
	}

	const returnToSlotSelectionFromLogin = () => {
		setCanContinueWithoutLogin(false)
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)

		setSelectedFormValues((prev) => {
			if (isGroupReservation) {
				return {
					...prev,
					[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]: {
						...prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION],
						eventID: null,
						timeSlot: {
							timeFrom: '',
							timeTo: '',
							employeeID: ''
						}
					}
				}
			}

			return {
				...prev,
				[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]: {
					...prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION],
					timeSlot: {
						timeFrom: '',
						timeTo: '',
						employeeID: '',
						dayPeriod: RESERVATIONS_TIME_SLOTS.MORNING
					}
				}
			}
		})

		if (isGroupReservation && selectedServiceData?.id) {
			const groupSelectionData = selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]
			fetchGroupSlots(salonData.id, {
				serviceID: selectedServiceData.id,
				employeeID: groupSelectionData.employeeID,
				dateFrom: dayjs().format(DEFAULT_DATE_FORMAT)
			})
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION)
			return
		}

		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION)
	}

	const onViewReservationsSummary = useCallback(() => {
		setHasCartReservationItems(true)
		setCanContinueWithoutLogin(true)
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY)
	}, [setCurrentStep])

	const handleCloseReservationBookingModal = (closeType: ReservationBookingModalCloseType | null = 'button_x', gaLabel?: string) => {
		if (currentStep && closeType) {
			const event = getModalClosedEvent({
				reservationStep: currentStep,
				interaction: closeType,
				elementType: gaLabel
			})
			pushToDataLayer(event)
		}

		onClose()
		setSelectedFormValues(DEFAULT_SELECTED_FORM_VALUES({ defaultPhonePrefixCountryCode, serviceEmployees: selectedServiceData?.employees || [], initData }))
		setSelectedServiceData(null)
		setSelectedIndustryData(null)
		setCanContinueWithoutLogin(false)
		setCurrentStep(null)
		setSubmittedReservationData(null)
		setIsAddingAnotherServiceFromSummary(false)
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)
		setHasCartReservationItems(false)
		setHasAcknowledgedUnavailableServices(false)

		clearSnapshot()
	}

	const shouldConfirmBeforeClose = () => {
		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.SUMMARY) {
			return false
		}

		return hasCartReservationItems
	}

	const requestLeaveReservation = () => {
		setIsLeaveReservationModalOpen(true)
	}

	const requestCloseReservationBookingModal = (closeType: ReservationBookingModalCloseType | null = 'button_x', gaLabel?: string) => {
		// LoginScreen after Step2 — cart not created yet; return to slot selection, no confirm dialog, no DELETE.
		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY && !canContinueWithoutLogin) {
			returnToSlotSelectionFromLogin()
			return
		}

		if (!shouldConfirmBeforeClose()) {
			handleCloseReservationBookingModal(closeType, gaLabel)
			return
		}

		setIsLeaveReservationModalOpen(true)
	}

	const handleLeaveReservationModalClose = () => {
		if (isLeavingReservation) {
			return
		}

		setIsLeaveReservationModalOpen(false)
	}

	const onCartExpired = () => {
		const shouldNavigateToServiceSelection = hasServicesSelectionStep

		// Cart is already released on BE — skip leave-reservation confirm and DELETE on close.
		setHasCartReservationItems(false)
		setCanContinueWithoutLogin(false)
		setIsAddingAnotherServiceFromSummary(false)
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)
		setHasAcknowledgedUnavailableServices(false)
		setCartReservationDeviceData({ cartActive: false })

		if (shouldNavigateToServiceSelection) {
			// Fresh reservation after cart expiry — clear service and form state so a new booking
			// does not inherit values from the previous one (parameter, date, time, note, etc.).
			// Also prevents stale prefill when the user picks the same service again: onServiceCardButtonClick
			// only resets selectedFormValues when selectedServiceData is null or the service ID changes.
			setSelectedServiceData(null)
			setSelectedFormValues(
				DEFAULT_SELECTED_FORM_VALUES({
					defaultPhonePrefixCountryCode,
					serviceEmployees: [],
					initData: null
				})
			)
			setAvailableGroupSlots(null)
			setGroupSlotsError(false)
			clearSnapshot()
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION)
			return
		}

		handleCloseReservationBookingModal('button_close')
	}

	const onBackToEmployeeAndDateSelection = async (cartItem: GetCartResponse['cart']['cartReservationItems'][number]) => {
		setIsEditingReservationFromSummary(true)
		setEditingCartReservationItemID(cartItem.id)
		// Login/guest step is only required before the first cart item exists.
		if (!hasCartReservationItems) {
			setCanContinueWithoutLogin(false)
		}

		const isCartItemGroup = cartItem.service.type === SERVICE_TYPE.CUSTOM_GROUP
		const endTime = cartItem.end.time ?? ''

		if (selectedServiceData?.id !== cartItem.service.id) {
			const { serviceData, industryData } = await getSelectedServiceAndIndustryData(
				cartItem.service.id,
				selectedIndustryData?.industryID ?? initIndustryID,
				salonServicesData.groupedServicesByCategory
			)

			if (serviceData) {
				setSelectedServiceData(serviceData)
				setSelectedIndustryData(industryData)
			}
		}

		if (isCartItemGroup) {
			// The edited event is already in the cart, so BE returns it as hasUserCartReservationItem (disabled in the list)
			// and PUT with the same event fails with 409. Keep only the employee preselected; the user must pick another event.
			setSelectedFormValues((prev) => ({
				...prev,
				[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]: {
					...prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION],
					employeeID: cartItem.employee.id,
					date: cartItem.start.date,
					eventID: null,
					timeSlot: {
						timeFrom: '',
						timeTo: '',
						employeeID: ''
					}
				}
			}))
			fetchGroupSlots(salonData.id, {
				serviceID: cartItem.service.id,
				employeeID: cartItem.employee.id,
				dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
				page: 1
			})
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION)
			return
		}

		setSelectedFormValues((prev) => ({
			...prev,
			[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION]: {
				...prev[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION],
				serviceCategoryParameterValueID: cartItem.serviceCategoryParameterValue?.id ?? ''
			},
			[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]: {
				employeeID: cartItem.employee.id,
				date: cartItem.start.date,
				timeSlot: {
					timeFrom: cartItem.start.time,
					timeTo: endTime,
					employeeID: cartItem.employee.id,
					dayPeriod: prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION].timeSlot.dayPeriod
				}
			}
		}))
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION)
	}

	/**
	 * Resets the time slot selection and navigates back to the employee/date step after leaving.
	 * Uses the last cart item when available; otherwise falls back to the service already selected in modal state
	 * (e.g. login screen after Step2 before the first cart item is created).
	 */
	const onLeaveReservationFromSummary = async (lastCartItem?: GetCartResponse['cart']['cartReservationItems'][number]) => {
		// Cart was cleared on BE (or was never created) — guest must POST again via "Continue without account".
		setCanContinueWithoutLogin(false)
		setHasCartReservationItems(false)
		setIsEditingReservationFromSummary(false)
		setEditingCartReservationItemID(null)
		setHasAcknowledgedUnavailableServices(false)

		// When cart failed to load, fall back to the service already selected in modal state.
		const isLastItemGroup = lastCartItem ? lastCartItem.service.type === SERVICE_TYPE.CUSTOM_GROUP : isGroupReservation
		const serviceID = lastCartItem?.service.id ?? selectedServiceData?.id
		const employeeAndDateGroupSelectionData = selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]

		// Switch modal context to the last cart service when the user had multiple services in the cart.
		if (lastCartItem && selectedServiceData?.id !== lastCartItem.service.id) {
			const { serviceData, industryData } = await getSelectedServiceAndIndustryData(
				lastCartItem.service.id,
				selectedIndustryData?.industryID ?? initIndustryID,
				salonServicesData.groupedServicesByCategory
			)

			if (serviceData) {
				setSelectedServiceData(serviceData)
				setSelectedIndustryData(industryData)
			}
		}

		if (lastCartItem) {
			const isCartItemGroup = lastCartItem.service.type === SERVICE_TYPE.CUSTOM_GROUP

			if (isCartItemGroup) {
				setSelectedFormValues((prev) => ({
					...prev,
					[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]: {
						employeeID: lastCartItem.employee.id,
						date: lastCartItem.start.date,
						eventID: null,
						timeSlot: {
							timeFrom: '',
							timeTo: '',
							employeeID: ''
						}
					}
				}))
				fetchGroupSlots(salonData.id, {
					serviceID: lastCartItem.service.id,
					employeeID: lastCartItem.employee.id,
					dateFrom: dayjs().format(DEFAULT_DATE_FORMAT),
					page: 1
				})
				setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION)
				return
			}

			setSelectedFormValues((prev) => ({
				...prev,
				[RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION]: {
					serviceCategoryParameterValueID: lastCartItem.serviceCategoryParameterValue?.id ?? ''
				},
				[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]: {
					employeeID: lastCartItem.employee.id,
					date: lastCartItem.start.date,
					timeSlot: {
						timeFrom: '',
						timeTo: '',
						employeeID: '',
						dayPeriod: prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION].timeSlot.dayPeriod
					}
				}
			}))
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION)
			return
		}

		// Keep the previously selected date and employee, but clear the time slot so nothing appears selected.
		setSelectedFormValues((prev) => {
			if (isLastItemGroup) {
				return {
					...prev,
					[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]: {
						...prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION],
						eventID: null,
						timeSlot: {
							timeFrom: '',
							timeTo: '',
							employeeID: ''
						}
					}
				}
			}

			return {
				...prev,
				[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]: {
					...prev[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION],
					timeSlot: {
						timeFrom: '',
						timeTo: '',
						employeeID: '',
						dayPeriod: RESERVATIONS_TIME_SLOTS.MORNING
					}
				}
			}
		})

		if (isLastItemGroup) {
			if (serviceID) {
				fetchGroupSlots(salonData.id, {
					serviceID,
					employeeID: employeeAndDateGroupSelectionData.employeeID,
					dateFrom: dayjs().format(DEFAULT_DATE_FORMAT)
				})
			}
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION)
			return
		}

		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION)
	}

	const handleLeaveReservationConfirm = async () => {
		setIsLeavingReservation(true)

		try {
			const lastCartItem = await clearServerCartAndGetLastItem()
			setCartReservationDeviceData({ cartActive: false })
			await onLeaveReservationFromSummary(lastCartItem)
			setIsAddingAnotherServiceFromSummary(false)
			setIsLeaveReservationModalOpen(false)
		} catch {
			setIsLeaveReservationModalOpen(false)
		} finally {
			setIsLeavingReservation(false)
		}
	}

	const onRedirectToHomepageButtonClick = () => {
		if (currentStep) {
			const event = getModalClosedEvent({
				reservationStep: currentStep,
				interaction: 'button_close'
			})
			pushToDataLayer(event)
		}

		clearSnapshot()
	}

	const onBackButtonClick = () => {
		if (currentStep) {
			const event = getModalBackButtonEvent({ reservationStep: currentStep })
			pushToDataLayer(event)
		}

		if (
			isEditingReservationFromSummary &&
			(currentStep === RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION ||
				currentStep === RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION)
		) {
			onBackFromEditReservation()
			return
		}

		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.SUMMARY) {
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION)
			return
		}

		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION) {
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY)
			return
		}

		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION && isAddingAnotherServiceFromSummary) {
			setIsAddingAnotherServiceFromSummary(false)
			setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY)
			return
		}

		if (
			currentStep === RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION ||
			currentStep === RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION
		) {
			if (hasParameterSelectionStep) {
				setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION)
				return
			}

			if (hasServicesSelectionStep) {
				setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION)
				return
			}
		}

		if (currentStep === RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION) {
			if (hasServicesSelectionStep) {
				setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION)
				return
			}
		}
		// Fallback: do tohto stavu by sa to aplikacne nemalo dostat
		setCurrentStep(RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION)
	}

	const onLoginWrap = () => {
		pushToDataLayer(getAccountEvent({ name: 'sign_in', interaction: 'button_sign_in' }))
		onLogin()
	}

	const onRegisterWrap = () => {
		pushToDataLayer(getAccountEvent({ name: 'sign_up', interaction: 'button_sign_up' }))
		onRegister()
	}

	const submitGuestCartReservation = async () => {
		if (!selectedServiceData) {
			setCanContinueWithoutLogin(true)
			clearSnapshot()
			return
		}

		setIsContinueAsGuestLoading(true)

		try {
			if (isGroupReservation) {
				const { eventID } = selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION]

				if (!eventID) {
					showNotifications([{ type: MSG_TYPE.ERROR, message: ERROR_MESSAGES().GENERAL_ERROR }])
					return
				}

				if (editingCartReservationItemID) {
					await submitPutCartReservationRequest({
						putCartReservations: apiBrowser.b2c.putCartGroupReservation,
						requestBody: getPutCartGroupReservationRequestBody(editingCartReservationItemID, eventID),
						cartReservationDeviceData,
						setCartReservationDeviceData
					})
				} else {
					await submitCartReservationRequest({
						postCartReservations: apiBrowser.b2c.postCartGroupReservations,
						requestBody: getCartGroupReservationRequestBody(eventID),
						cartReservationDeviceData,
						setCartReservationDeviceData
					})
					onCartReservationItemAdded()
				}
			} else {
				const requestSelectionData = selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION]
				const selectedEmployeeID = requestSelectionData.employeeID || requestSelectionData.timeSlot.employeeID

				const cartReservationArgs = {
					date: requestSelectionData.date,
					timeFrom: requestSelectionData.timeSlot.timeFrom,
					timeTo: requestSelectionData.timeSlot.timeTo,
					salonID: salonData.id,
					employeeID: selectedEmployeeID,
					serviceID: selectedServiceData.id,
					serviceCategoryParameterValueID: selectedServiceCategoryParameterValueData?.id ?? null
				}

				if (editingCartReservationItemID) {
					await submitPutCartReservationRequest({
						putCartReservations: apiBrowser.b2c.putCartReservation,
						requestBody: getPutCartReservationRequestBody(editingCartReservationItemID, cartReservationArgs),
						cartReservationDeviceData,
						setCartReservationDeviceData
					})
				} else {
					await submitCartReservationRequest({
						postCartReservations: apiBrowser.b2c.postCartReservations,
						requestBody: getCartReservationRequestBody(cartReservationArgs),
						cartReservationDeviceData,
						setCartReservationDeviceData
					})
					onCartReservationItemAdded()
				}
			}
			clearCartEditState()
			setCanContinueWithoutLogin(true)
			clearSnapshot()
		} catch (error) {
			const errorCode = getCartReservationSubmitErrorCode(error)

			if (errorCode === CART_RESERVATION_SUBMIT_ERROR_CODE.CART_IS_FULL) {
				setIsFullCartModalOpen(true)
				return
			}

			if (errorCode === CART_RESERVATION_SUBMIT_ERROR_CODE.ALREADY_BOOKED) {
				setIsServiceAlreadyBookedModalOpen(true)
				return
			}

			showErrorNotifications(error, { force: true })
		} finally {
			setIsContinueAsGuestLoading(false)
		}
	}

	const handleFullCartModalClose = () => {
		setIsFullCartModalOpen(false)
	}

	const handleFullCartModalViewReservations = () => {
		setIsFullCartModalOpen(false)
		onViewReservationsSummary()
	}

	const handleGuestServiceAlreadyBookedModalConfirm = () => {
		setIsServiceAlreadyBookedModalOpen(false)
		setCurrentStep(
			isGroupReservation ? RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION : RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION
		)
	}

	const onContinueAsGuest = async () => {
		pushToDataLayer(getAccountEvent({ name: 'wo_account', interaction: 'text_wo_account' }))
		await submitGuestCartReservation()
	}

	return (
		<SC.ModalWrapper ref={containerRef}>
			<Modal
				show={isOpen}
				className={RESERVATION_BOOKING_MODAL_CLASS_NAME}
				layout={'fullScreenOnMobile'}
				hideDefaultCloseButton
				onClose={() => requestCloseReservationBookingModal('button_x')}
				breakpoint={ModalModel.ModalBreakpoints.md}
				disableOnBlurClose
				disableCloseOnEscape
				noBorders
				ariaLabelledBy={RESERVATION_BOOKING_MODAL_TITLE_ID}
			>
				<SC.Content aria-busy={initialLoading}>
					{(() => {
						if (initialLoading) {
							return <ReservationBookingModalSkeleton onClose={requestCloseReservationBookingModal} />
						}

						if (isLoadingError) {
							return <SC.StyledError />
						}

						if (currentStep === RESERVATION_BOOKING_MODAL_STEP.SERVICE_SELECTION && selectedIndustryData) {
							return (
								<Step0ServiceSelection
									selectedIndustryData={selectedIndustryData}
									salonServicesData={salonServicesData}
									fallbackHeaderImage={fallbackHeaderImage}
									phoneNumber={phoneNumber}
									onClose={requestCloseReservationBookingModal}
									onBackButtonClick={isAddingAnotherServiceFromSummary ? onBackButtonClick : undefined}
									onServiceCardButtonClick={onServiceCardButtonClick}
								/>
							)
						}
						if (
							currentStep === RESERVATION_BOOKING_MODAL_STEP.SERVICE_PARAMETER_SELECTION &&
							selectedServiceCategoryParameterData &&
							selectedServiceData
						) {
							return (
								<Step1ServiceParameterSelection
									selectedFormValues={selectedFormValues}
									selectedIndustryID={selectedIndustryData?.industryID}
									selectedServiceData={selectedServiceData}
									serviceCategoryParameterData={selectedServiceCategoryParameterData}
									headerImage={headerImage}
									onClose={requestCloseReservationBookingModal}
									onBackButtonClick={hasServicesSelectionStep ? onBackButtonClick : undefined}
									handleSubmitParameterSelectionFrom={handleSubmitParameterSelectionFrom}
									cmsData={cmsData.parameterSelectionBannerData}
								/>
							)
						}

						if (currentStep === RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_SELECTION && selectedServiceData && !isGroupReservation) {
							return (
								<Step2EmployeeAndDateSelection
									salonID={salonData.id}
									selectedFormValues={selectedFormValues}
									selectedIndustryID={selectedIndustryData?.industryID}
									selectedServiceData={selectedServiceData}
									selectedServiceCategoryParameterValueData={selectedServiceCategoryParameterValueData}
									headerImage={headerImage}
									isAddingAnotherServiceFromSummary={isAddingAnotherServiceFromSummary}
									maxCountCartReservations={configData.maxCountCartReservations}
									onViewReservationsSummary={onViewReservationsSummary}
									editingCartReservationItemID={editingCartReservationItemID}
									onPersistCartReservation={persistEmployeeAndDateCartReservation}
									onClose={requestCloseReservationBookingModal}
									// Show back only when there is a previous step: parameter/service selection in the normal flow,
									// or return to cart summary when the user opened this step via "Edit" on the reservations summary.
									// Hide it when the modal was opened with a preselected service (initServiceID) and earlier steps were skipped.
									onBackButtonClick={
										isEditingReservationFromSummary || hasParameterSelectionStep || hasServicesSelectionStep ? onBackButtonClick : undefined
									}
									handleSubmitEmployeeAndDateSelectionForm={handleSubmitEmployeeAndDateSelectionForm}
									initAvResTimeSlotDayPart={initAvResTimeSlotDayPart}
								/>
							)
						}

						if (currentStep === RESERVATION_BOOKING_MODAL_STEP.EMPLOYEE_AND_DATE_GROUP_SELECTION && selectedServiceData && isGroupReservation) {
							return (
								<Step2EmployeeAndDateSelectionGroup
									selectedServiceData={selectedServiceData}
									selectedServiceCategoryParameterValueData={selectedServiceCategoryParameterValueData}
									headerImage={headerImage}
									isAddingAnotherServiceFromSummary={isAddingAnotherServiceFromSummary}
									maxCountCartReservations={configData.maxCountCartReservations}
									onViewReservationsSummary={onViewReservationsSummary}
									editingCartReservationItemID={editingCartReservationItemID}
									onPersistGroupCartReservation={persistEmployeeAndDateGroupCartReservation}
									onClose={requestCloseReservationBookingModal}
									selectedIndustryID={selectedIndustryData?.industryID}
									salonID={salonData.id}
									setIsFetching={setIsFetchingGroupSlots}
									handleSubmitEmployeeAndDateGroupSelectionForm={handleSubmitEmployeeAndDateGroupSelectionForm}
									selectedFormValues={selectedFormValues}
									onBackButtonClick={
										isEditingReservationFromSummary || hasParameterSelectionStep || hasServicesSelectionStep ? onBackButtonClick : undefined
									}
									availableGroupSlots={availableGroupSlots}
									isFetching={isFetchingGroupSlots}
									loadingError={groupSlotsError}
									fetchGroupSlots={fetchGroupSlots}
								/>
							)
						}

						return (
							<LoginScreen
								onContinueAsGuest={onContinueAsGuest}
								onLogin={onLoginWrap}
								onRegister={onRegisterWrap}
								onClose={() => requestCloseReservationBookingModal('button_x', 'sso')} // @see https://goodrequest.atlassian.net/browse/NOT-11439?focusedCommentId=104125
								cmsData={cmsData.loginAndRegistrationBannerData}
								isContinueAsGuestLoading={isContinueAsGuestLoading}
								showChildren={canContinueWithoutLogin}
							>
								{currentStep === RESERVATION_BOOKING_MODAL_STEP.BOOKING_CONFIRMATION && selectedServiceData && (
									<Step3BookingConfirmation
										configData={configData}
										documentsData={documents}
										selectedFormValues={selectedFormValues}
										selectedIndustryID={selectedIndustryData?.industryID}
										selectedServiceData={selectedServiceData}
										selectedServiceCategoryParameterValueData={selectedServiceCategoryParameterValueData}
										headerImage={salonHeaderImage}
										onClose={requestCloseReservationBookingModal}
										onBackButtonClick={onBackButtonClick}
										onUnavailableServicesAcknowledged={onUnavailableServicesAcknowledged}
										onSubmitBookingConfirmationForm={onSubmitBookingConfirmationForm}
										onCartExpired={onCartExpired}
										onLogin={onLogin}
										onRegister={onRegister}
									/>
								)}
								{currentStep === RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY && selectedServiceData && (
									<Step3ReservationsSummary
										configData={configData}
										headerImage={salonHeaderImage}
										selectedFormValues={selectedFormValues}
										onBackToEmployeeAndDateSelection={onBackToEmployeeAndDateSelection}
										onRequestLeaveReservation={requestLeaveReservation}
										isLeaveReservationModalOpen={isLeaveReservationModalOpen}
										onCartReservationItemsChange={onCartReservationItemsChange}
										onSubmitReservationsSummaryForm={onSubmitReservationsSummaryForm}
										hasAcknowledgedUnavailableServices={hasAcknowledgedUnavailableServices}
										onUnavailableServicesAcknowledged={onUnavailableServicesAcknowledged}
										onInitNoteFromCart={onInitNoteFromCart}
										onAddAnotherService={onAddAnotherService}
										onCartExpired={onCartExpired}
									/>
								)}
								{currentStep === RESERVATION_BOOKING_MODAL_STEP.SUMMARY && submittedReservationData && selectedServiceData && (
									<Step4Summary
										onClose={handleCloseReservationBookingModal}
										onRedirectToHomepage={onRedirectToHomepageButtonClick}
										submittedReservationData={submittedReservationData}
										selectedServiceData={selectedServiceData}
										cmsData={cmsData.thankYouPageBannerData}
									/>
								)}
							</LoginScreen>
						)
					})()}
				</SC.Content>
			</Modal>
			<ReservationBookingDialog
				isOpen={isLeaveReservationModalOpen}
				onClose={handleLeaveReservationModalClose}
				showHeaderCloseButton
				isHeaderCloseButtonDisabled={isLeavingReservation}
				title={formatMessage({ id: 'Leave reservation', defaultMessage: 'Leave reservation' })}
				description={formatMessage({
					id: 'If you leave the reservation now, the selected time slots will no longer be reserved for you. Do you want to leave the reservation?',
					defaultMessage:
						'If you leave the reservation now, the selected time slots will no longer be reserved for you. Do you want to leave the reservation?'
				})}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.secondary}
								fullWidth
								disabled={isLeavingReservation}
								onClick={handleLeaveReservationModalClose}
							>
								{formatMessage({ id: 'Cancel', defaultMessage: 'Cancel' })}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.primary}
								fullWidth
								disabled={isLeavingReservation}
								aria-busy={isLeavingReservation}
								onClick={handleLeaveReservationConfirm}
							>
								{isLeavingReservation ? (
									<>
										<Spinner size={16} color={'icon.disabled'} aria-hidden />
										<VisuallyHidden>{formatMessage({ id: 'Yes, leave', defaultMessage: 'Yes, leave' })}</VisuallyHidden>
									</>
								) : (
									formatMessage({ id: 'Yes, leave', defaultMessage: 'Yes, leave' })
								)}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isFullCartModalOpen}
				onClose={handleFullCartModalClose}
				title={formatMessage({ id: "Couldn't add service", defaultMessage: "Couldn't add service" })}
				description={formatMessage(
					{
						id: 'You already have {maxCountReservations} services in your reservation. Try deleting a service first, then add this one.',
						defaultMessage: 'You already have {maxCountReservations} services in your reservation. Try deleting a service first, then add this one.'
					},
					{ maxCountReservations: configData.maxCountCartReservations }
				)}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button type={'button'} buttonStyle={ButtonModel.Styles.secondary} fullWidth onClick={handleFullCartModalClose}>
								{formatMessage({ id: 'Close booking modal', defaultMessage: 'Close' })}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleFullCartModalViewReservations}>
								{formatMessage({ id: 'View reservations', defaultMessage: 'View reservations' })}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isServiceAlreadyBookedModalOpen}
				onClose={handleGuestServiceAlreadyBookedModalConfirm}
				title={formatMessage({ id: 'Service has been booked!', defaultMessage: 'Service has been booked!' })}
				description={formatMessage({
					id: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.',
					defaultMessage: 'Some of your services have been booked. Please select another time slot, change the employee or choose another service.'
				})}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleGuestServiceAlreadyBookedModalConfirm}>
						{formatMessage({ id: 'OK', defaultMessage: 'OK' })}
					</Button>
				}
			/>
		</SC.ModalWrapper>
	)
}

export default ReservationBookingModal
